/**
 * Split an assistant reply into at most `maxChunks` chat bubbles.
 *
 * Rules: paragraphs (blank-line separated) are the natural boundary; if there are more
 * paragraphs than bubbles the tail is merged into the last bubble; a single very long
 * paragraph is split at sentence boundaries into balanced bubbles.
 */
export const MAX_REPLY_CHUNKS = 3;
const LONG_PARAGRAPH_CHARS = 350;

const SENTENCE_END = /(?<=[.!?؟。!?…])\s+/u;

/**
 * Paragraph boundary rule shared with the streaming client: blank-line separated paragraphs,
 * at most `maxChunks`, the tail merged into the last one. The client splits the streamed draft
 * with this exact rule so the final message never reflows from one card into two.
 */
export function splitParagraphs(text: string, maxChunks = MAX_REPLY_CHUNKS): string[] {
  const paragraphs = text
    .split(/\n\s*\n/u)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paragraphs.length > maxChunks) {
    return [...paragraphs.slice(0, maxChunks - 1), paragraphs.slice(maxChunks - 1).join('\n\n')];
  }
  return paragraphs;
}

export function chunkReply(text: string, maxChunks = MAX_REPLY_CHUNKS): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  if (maxChunks <= 1) return [trimmed];

  const paragraphs = splitParagraphs(trimmed, maxChunks);

  let chunks: string[];
  if (paragraphs.length === 1 && trimmed.length > LONG_PARAGRAPH_CHARS) {
    chunks = splitBalanced(splitSentences(trimmed), maxChunks);
  } else {
    chunks = paragraphs;
  }
  return chunks.filter(Boolean);
}

function splitSentences(text: string): string[] {
  return text
    .split(SENTENCE_END)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Group sentences into at most `n` pieces of roughly equal length, preserving order. */
function splitBalanced(sentences: string[], n: number): string[] {
  if (sentences.length <= n) return sentences;
  const total = sentences.reduce((sum, s) => sum + s.length, 0);
  const target = total / n;
  const out: string[] = [];
  let current: string[] = [];
  let currentLen = 0;
  for (const sentence of sentences) {
    const remainingSlots = n - out.length;
    if (current.length > 0 && currentLen + sentence.length > target && remainingSlots > 1) {
      out.push(current.join(' '));
      current = [];
      currentLen = 0;
    }
    current.push(sentence);
    currentLen += sentence.length;
  }
  if (current.length) out.push(current.join(' '));
  return out;
}

/**
 * Split a long transcript (a voice note) into pieces of at most `maxChars` at sentence
 * boundaries, so it can be stored and shown in readable parts without cutting mid-sentence.
 */
export function chunkTranscript(text: string, maxChars = 1_200): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  if (trimmed.length <= maxChars) return [trimmed];
  const out: string[] = [];
  let current = '';
  for (const sentence of splitSentences(trimmed)) {
    if (current && current.length + 1 + sentence.length > maxChars) {
      out.push(current);
      current = sentence;
    } else {
      current = current ? `${current} ${sentence}` : sentence;
    }
    // A single sentence longer than the cap is hard-split.
    while (current.length > maxChars) {
      out.push(current.slice(0, maxChars));
      current = current.slice(maxChars);
    }
  }
  if (current) out.push(current);
  return out;
}
